import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, Field } from '@promtver/ui-kit';
import { B2BRequestKind, B2BRequestStatus, UserRole } from '@promtver/contracts';

import { assignRole, getB2BRequests, getEnterprisesForModeration } from '../../api/admin';
import { errorMessage } from '../../api/errors';
import { AppShell } from '../../components/AppShell';
import { AsyncState } from '../../components/AsyncState';
import { KeyValue } from '../../components/KeyValue';
import { Select } from '../../components/Select';
import { ru } from '../../i18n/ru';
import { formatDateTimeLocal } from '../../lib/datetime';

/** Значение «предприятие выбрано вручную» — в списке справочника такого id нет. */
const MANUAL_ENTERPRISE = '__manual__';

function requestKindLabel(kind: B2BRequestKind | string): string {
  return kind === B2BRequestKind.BusinessVisit ? ru.requests.kindBusinessVisit : ru.requests.kindOnboarding;
}

function requestStatusLabel(status: B2BRequestStatus | string): string {
  switch (status) {
    case B2BRequestStatus.InProgress:
      return ru.requests.statusInProgress;
    case B2BRequestStatus.Answered:
      return ru.requests.statusAnswered;
    case B2BRequestStatus.Rejected:
      return ru.requests.statusRejected;
    default:
      return ru.requests.statusNew;
  }
}

/**
 * Журнал заявок предприятий (FR-B2B-02) и назначение представителя модератором
 * своего объекта (FR-ADM-04).
 *
 * Заявка на подключение, оставленная представителем, содержит созданную
 * учётную запись — по ней выдаём роль object_moderator с привязкой к объекту.
 */
export function EnterpriseRequestsPage() {
  const queryClient = useQueryClient();
  const [enterpriseChoice, setEnterpriseChoice] = useState<Record<string, string>>({});
  const [manualEnterpriseId, setManualEnterpriseId] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const requestsQuery = useQuery({
    queryKey: ['admin', 'b2b-requests'],
    queryFn: () => getB2BRequests(),
  });

  const enterprisesQuery = useQuery({
    queryKey: ['admin', 'enterprises', 'all-for-requests'],
    queryFn: () => getEnterprisesForModeration(undefined),
  });

  const assignMutation = useMutation({
    mutationFn: ({ userId, enterpriseId }: { userId: string; enterpriseId: string }) =>
      assignRole(userId, UserRole.ObjectModerator, enterpriseId),
    onSuccess: async () => {
      setError(null);
      setNotice(ru.requests.assigned);
      await queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
    onError: (cause) => setError(errorMessage(cause, ru.requests.errorAssign)),
  });

  const requests = requestsQuery.data?.items ?? [];
  const enterprises = enterprisesQuery.data?.items ?? [];

  /**
   * Идентификатор предприятия для заявки: выбранный из справочника либо
   * введённый вручную — в справочнике может не быть нужной карточки.
   */
  const resolveEnterpriseId = (requestId: string): string => {
    const choice = (enterpriseChoice[requestId] ?? '').trim();
    if (choice === MANUAL_ENTERPRISE) {
      return (manualEnterpriseId[requestId] ?? '').trim();
    }
    return choice;
  };

  return (
    <AppShell title={ru.requests.title} subtitle={ru.requests.subtitle}>
      {error ? (
        <p role="alert" className="text-xs text-danger">
          {error}
        </p>
      ) : null}
      {notice ? (
        <p role="status" className="text-xs text-success">
          {notice}
        </p>
      ) : null}

      <Card title={ru.requests.title}>
        <AsyncState
          isLoading={requestsQuery.isLoading}
          isError={requestsQuery.isError}
          isEmpty={requests.length === 0}
          emptyTitle={ru.requests.empty}
          emptyDescription={ru.requests.emptyDescription}
          onRetry={() => void requestsQuery.refetch()}
        >
          <ul className="flex flex-col gap-3">
            {requests.map((request) => {
              // Константы нужны для сужения типов внутри обработчиков кнопок.
              const userId = request.userId;
              const enterpriseId = resolveEnterpriseId(request.id);
              const isManual = (enterpriseChoice[request.id] ?? '') === MANUAL_ENTERPRISE;

              return (
                <li key={request.id} className="rounded-card border border-line p-3">
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <strong className="text-sm">
                      {request.legalName ?? ru.common.notSpecified}
                    </strong>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge tone="steel">{requestKindLabel(request.kind)}</Badge>
                      <Badge tone={request.status === B2BRequestStatus.New ? 'accent' : 'default'}>
                        {requestStatusLabel(request.status)}
                      </Badge>
                    </div>
                  </div>

                  <div className="flex flex-col gap-1">
                    <KeyValue label={ru.requests.legalName} value={request.legalName ?? ru.common.notSpecified} />
                    <KeyValue label={ru.requests.contact} value={request.contactName} />
                    <KeyValue label={ru.common.phone} value={request.contactPhone} mono />
                    <KeyValue label={ru.common.email} value={request.email ?? ru.common.notSpecified} mono />
                    <KeyValue label={ru.common.district} value={request.district ?? ru.common.notSpecified} />
                    {request.inn ? <KeyValue label={ru.requests.inn} value={request.inn} mono /> : null}
                    {request.slotsPerWeek ? (
                      <KeyValue label={ru.requests.slotsPerWeek} value={request.slotsPerWeek} />
                    ) : null}
                    {request.message ? (
                      <KeyValue label={ru.requests.message} value={request.message} />
                    ) : null}
                    <KeyValue label={ru.requests.createdAt} value={formatDateTimeLocal(request.createdAt)} />
                  </div>

                  <div className="mt-3 flex flex-col gap-2">
                    {userId ? (
                      <>
                        <Select
                          label={ru.requests.enterprise}
                          value={enterpriseChoice[request.id] ?? ''}
                          onChange={(value) =>
                            setEnterpriseChoice((previous) => ({ ...previous, [request.id]: value }))
                          }
                          options={[
                            { value: '', label: ru.requests.enterpriseSelect },
                            ...enterprises.map((enterprise) => ({
                              value: enterprise.id,
                              label: enterprise.name,
                            })),
                            { value: MANUAL_ENTERPRISE, label: ru.requests.enterpriseManual },
                          ]}
                        />
                        {isManual ? (
                          <Field
                            label={ru.requests.enterpriseManualId}
                            value={manualEnterpriseId[request.id] ?? ''}
                            onChange={(value) =>
                              setManualEnterpriseId((previous) => ({ ...previous, [request.id]: value }))
                            }
                          />
                        ) : null}
                        {enterprises.length === 0 && !isManual ? (
                          <p className="text-xs text-muted">{ru.requests.enterpriseEmpty}</p>
                        ) : null}
                        <Button
                          tone="accent"
                          disabled={assignMutation.isPending || !enterpriseId}
                          onClick={() => assignMutation.mutate({ userId, enterpriseId })}
                        >
                          {ru.requests.assignModerator}
                        </Button>
                      </>
                    ) : (
                      <p className="text-xs text-muted">{ru.requests.noUser}</p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </AsyncState>
      </Card>
    </AppShell>
  );
}
